import { sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';

const CHECK_IF_INTEGER_REGEXP = /^\d+$/gm;

import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

import { COLUMN_TYPES, VIEW_MODES } from '../datagrid/constants';
import {
  renderCategoryCell,
  renderIngredientQuantityCell,
  renderInputCell,
  renderInputHeader,
  renderNameCell,
  renderRecipeQuantityCell,
  renderStandardHeader,
  renderTotalCell,
  renderEditInputCell,
  renderInputCellWithTimeSlots,
  renderEditInputCellWithTimeSlots,
} from '../datagrid/customCellRenders';

const DayCellRenderByType = {
  [COLUMN_TYPES.PRODUCTS]: renderInputCell,
  [COLUMN_TYPES.RECIPES]: renderRecipeQuantityCell,
  [COLUMN_TYPES.INGREDIENTS]: renderIngredientQuantityCell,
};

const DayCellRenderByTypeWithTimeSlots = {
  [COLUMN_TYPES.PRODUCTS]: renderInputCellWithTimeSlots,
  [COLUMN_TYPES.RECIPES]: renderRecipeQuantityCell,
  [COLUMN_TYPES.INGREDIENTS]: renderIngredientQuantityCell,
};

const getTimeSlotsColumns = (isPTSCellEditable, productionTimeSlots, { type, dayColumnFields }) => {
  const columns = [];

  const today = moment().format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY); // Only used for moment format, the date is not used

  const sortedProductionTimeSlots = sortBy(productionTimeSlots, 'startTime');

  sortedProductionTimeSlots.forEach((pts) => {
    const formattedStartTime = moment(`${today} ${pts.startTime}`).format(
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE,
    );
    const formattedEndTime = moment(`${today} ${pts.endTime}`).format(
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE,
    );

    columns.push({
      ...dayColumnFields,
      field: pts.id,
      headerName: pts.name,
      headerSubTitle: `${formattedStartTime} - ${formattedEndTime}`,
      editable: isPTSCellEditable,
      disableColumnMenu: true,
      sortable: false,
      resizable: true,
      ...(type === COLUMN_TYPES.PRODUCTS
        ? { renderEditCell: renderEditInputCellWithTimeSlots }
        : {}),
      renderHeader: (props) => renderInputHeader(props),
      preProcessEditCellProps: ({ props }) => {
        if (!props.value) {
          // No need to check if it's an integer when it's null
          return { ...props, error: false };
        }

        // Not using Number.isInteger() because it allows numbers like 1.0 or 1. without anything behind
        const hasError = !new RegExp(CHECK_IF_INTEGER_REGEXP).test(`${props.value}`);
        return { ...props, error: hasError };
      },
    });
  });

  return columns;
};

const dateHasValidTimeSlots = (timeSlotsGroupedByDate, date) => {
  const timeSlotsOfTheDay = timeSlotsGroupedByDate[date] || [];

  if (!timeSlotsOfTheDay.length) {
    return false;
  }

  if (
    timeSlotsOfTheDay.length === 1 &&
    timeSlotsOfTheDay[0].name === null &&
    timeSlotsOfTheDay[0].active
  ) {
    return false;
  }

  const timeSlotsWithoutDefault = timeSlotsOfTheDay.filter(({ name }) => name !== null);

  return timeSlotsWithoutDefault.length > 0;
};

// Generate columns for each day over the period of selectedDates
export const generateDateColumns = (
  type,
  selectedDates,
  {
    editable = true,
    onZoomClick,
    timeSlotsGroupedByDate = {},
    viewMode = VIEW_MODES.CALENDAR,
    selectedDate = selectedDates[0],
    productionTimeSlots = [],
  } = {},
) => {
  const today = moment().format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

  const dayColumnFields = {
    type: 'number',
    minWidth: 148,
    disableReorder: true,
    filterable: false,
    headerAlign: 'center',
    align: 'center',
    renderCell:
      viewMode === VIEW_MODES.CALENDAR
        ? DayCellRenderByType[type]
        : DayCellRenderByTypeWithTimeSlots[type],
  };

  if (viewMode === VIEW_MODES.DAY) {
    const isPTSCellEditable = editable && moment(selectedDate).isSameOrAfter(today);

    return getTimeSlotsColumns(isPTSCellEditable, productionTimeSlots, { type, dayColumnFields });
  }

  const columns = [];

  selectedDates.forEach((date) => {
    const isCellEditable = editable && moment(date).isSameOrAfter(today);

    const showZoom = dateHasValidTimeSlots(timeSlotsGroupedByDate, date);

    columns.push({
      ...dayColumnFields,
      field: date,
      headerName: moment(date).format(DATE_DISPLAY_FORMATS.CONDENSED_SLASHED_DAY_MONTH),
      editable: isCellEditable,
      disableColumnMenu: true,
      sortable: false,
      ...(type === COLUMN_TYPES.PRODUCTS ? { renderEditCell: renderEditInputCell } : {}),
      renderHeader: (props) => renderInputHeader(props, onZoomClick, showZoom),
      preProcessEditCellProps: ({ props }) => {
        if (!props.value) {
          // No need to check if it's an integer when it's null
          return { ...props, error: false };
        }

        // Not using Number.isInteger() because it allows numbers like 1.0 or 1. without anything behind
        const hasError = !new RegExp(CHECK_IF_INTEGER_REGEXP).test(`${props.value}`);
        return { ...props, error: hasError };
      },
    });
  });

  return columns;
};

export const getColumns = (
  type,
  selectedDates,
  {
    onZoomClick,
    onRefreshClick,
    timeSlotsGroupedByDate = {},
    viewMode = VIEW_MODES.CALENDAR,
    selectedDate = selectedDates[0],
    productionTimeSlots = [],
    canEditSalesPlanning = false,
  } = {},
) => {
  const commonColumnsProps = {
    disableReorder: true,
    renderHeader: renderStandardHeader,
  };

  const nameColumn = {
    ...commonColumnsProps,
    field: 'name',
    headerName: i18next.t('GENERAL.NAME'),
    minWidth: 320,
  };

  const skuColumn = {
    field: 'sku',
  };

  const categoryColumn = {
    ...commonColumnsProps,
    field: 'category',
    headerName: i18next.t('GENERAL.CATEGORY'),
    minWidth: 200,
    renderCell: ({ row }) => renderCategoryCell(row.category),
    filterable: true,
    getApplyQuickFilterFn: (value) => {
      if (!value) {
        return () => true;
      }
      return (params) => {
        const categoryValue = params.value;
        const searchValue = value.toLowerCase();

        // Handle null/undefined values - show them when searching for "none" or the translated "none" value
        if (!categoryValue) {
          const translatedNoneValue = i18next.t('GENERAL.NONE_VALUE').toLowerCase();
          return translatedNoneValue.includes(searchValue);
        }

        // Handle regular category values
        return categoryValue.toLowerCase().includes(searchValue);
      };
    },
  };

  const totalColumn = {
    ...commonColumnsProps,
    field: 'total',
    type: 'number',
    headerName: i18next.t('GENERAL.TOTAL'),
    minWidth: 160,
    headerAlign: 'left',
    renderCell: (props) => renderTotalCell(props, type, { onRefreshClick }),
  };

  if (type === COLUMN_TYPES.PRODUCTS) {
    nameColumn['renderCell'] = ({ row }) =>
      renderNameCell(row.name, COLUMN_TYPES.PRODUCTS, {
        sku: row.sku,
        withImg: true,
        imgSrc: row.img,
      });

    categoryColumn.filterable = false;

    return [
      nameColumn,
      skuColumn,
      categoryColumn,
      ...generateDateColumns(COLUMN_TYPES.PRODUCTS, selectedDates, {
        onZoomClick,
        timeSlotsGroupedByDate,
        viewMode,
        selectedDate,
        productionTimeSlots,
        editable: canEditSalesPlanning,
      }),
      totalColumn,
    ];
  }

  nameColumn['renderCell'] = ({ row }) =>
    renderNameCell(row.name, type, { withImg: true, imgSrc: row.img });

  return [
    nameColumn,
    categoryColumn,
    ...generateDateColumns(type, selectedDates, {
      editable: false,
      onZoomClick,
      timeSlotsGroupedByDate,
      viewMode,
      selectedDate,
      productionTimeSlots,
    }),
    totalColumn,
  ];
};
